import { useInfiniteQuery } from '@tanstack/react-query';
import {
  CHAT_SEARCH_MAX_QUERY_LENGTH,
  chatSearchMinQueryLength,
  type ChatSearchMatchMode,
} from '@fias/platform-resources';
import { api, ApiError, type ChatMessageResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/** The mode search panels open in — matches text anywhere, like most search boxes. */
export const CHAT_SEARCH_DEFAULT_MATCH_MODE: ChatSearchMatchMode = 'contains';

/** True for the server's "search matched too broadly to finish" refusal. */
export function isSearchTooBroadError(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'SEARCH_TOO_BROAD';
}

/**
 * Normalizes the raw search inputs shared by both search hooks: trims and caps
 * the text, treats text below the mode's minimum as absent (so "de" + a sender
 * still searches by sender rather than sitting idle), and reports whether
 * anything is searchable at all.
 */
export function normalizeChatSearchInput(
  query: string,
  senderUsername: string | null,
  matchMode: ChatSearchMatchMode,
): { terms: string | undefined; from: string | undefined; isQueryable: boolean } {
  const trimmed = query.trim().slice(0, CHAT_SEARCH_MAX_QUERY_LENGTH);
  const from = senderUsername?.trim() || undefined;
  const terms = trimmed.length >= chatSearchMinQueryLength(matchMode) ? trimmed : undefined;
  return { terms, from, isQueryable: !!terms || !!from };
}

export interface UseChatMessageSearchResult {
  results: ChatMessageResponse[];
  /** Total matches in the channel, from the first page. */
  total: number;
  isLoading: boolean;
  isError: boolean;
  /** The server gave up because the search matched too broadly (422 SEARCH_TOO_BROAD). */
  isTooBroad: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

/**
 * Search over one channel's messages, cursor-paginated. Backed by
 * `GET /arches/chat/channels/:channelId/messages/search`, which accepts text
 * terms, a sender username, or both — so "everything Bob wrote here" is a search
 * with no text at all. `matchMode` picks whole-word or substring matching for
 * the text.
 *
 * Only runs while `enabled` (the panel is open) and at least one filter is
 * usable, so a closed panel or a half-typed word costs nothing. The caller is
 * responsible for debouncing `query`.
 */
export function useChatMessageSearch(
  channelId: string | null,
  query: string,
  senderUsername: string | null,
  enabled: boolean,
  matchMode: ChatSearchMatchMode = CHAT_SEARCH_DEFAULT_MATCH_MODE,
  pageSize = 25,
): UseChatMessageSearchResult {
  const { terms, from, isQueryable } = normalizeChatSearchInput(query, senderUsername, matchMode);

  const result = useInfiniteQuery({
    queryKey: queryKeys.chat.messageSearch(channelId ?? '', terms ?? '', from ?? '', matchMode),
    queryFn: ({ pageParam }) =>
      api.searchChatMessages(channelId!, terms ?? '', {
        from,
        match: matchMode,
        limit: pageSize,
        before: pageParam as string | undefined,
      }),
    initialPageParam: undefined as string | undefined,
    // The server decides whether more exists (it over-fetches by one) rather
    // than the client inferring it from a count — a count-vs-page mismatch
    // caused by a concurrent delete would otherwise strand "Load more" forever.
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: enabled && !!channelId && isQueryable,
    staleTime: 30_000,
  });

  return {
    results: result.data?.pages.flatMap((p) => p.messages) ?? [],
    // Only the first page carries a total; later pages report null.
    total: result.data?.pages[0]?.total ?? 0,
    // Nothing searchable isn't "loading" — it's idle, and the panel shows its
    // prompt rather than a spinner that would never resolve.
    isLoading: isQueryable && result.isLoading,
    isError: result.isError,
    isTooBroad: isSearchTooBroadError(result.error),
    hasNextPage: result.hasNextPage,
    isFetchingNextPage: result.isFetchingNextPage,
    fetchNextPage: () => void result.fetchNextPage(),
  };
}
